import isEqual from 'lodash.isequal'
import { useEffect, useState } from 'react'
import { googleMapsService } from 'services/googleMapsService'
import {
  Attachment,
  Attendee,
  DocType,
  EventDoc,
  EventLocation,
  EventNote,
  EventParticipantStatus,
  EventRecurrence,
  EventReminder,
  EventStatus,
  GroupDoc,
  LocationReminder,
  SimpleLabel,
  TaskType
} from 'types/api/changes'
import { CollectionType, Model } from 'types/model'

import {
  useForm,
  UseFormRegister,
  UseFormGetValues,
  Control,
  UseFormHandleSubmit
} from 'react-hook-form'
import dayjs from 'dayjs'
import Logger from 'services/Logger'
import Database from 'services/db/Database'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { is24meApp } from 'utils/appType'
import { isTrulyEmpty, removePrefix } from 'utils/object'
import { generateStartEndBasedOnTime, isAMPM } from 'utils/date'
import { localEventById, localEventByLocalId } from 'services/db/Queries/Event'
import { AppUIType } from 'components/providers/AppUITypeProvider'
import ParticipantSuggestion from 'model/models/ParticipantSuggestion'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'

import { groupToProvider, isConvertedGroupId, ProviderType } from 'utils/groups'
import { getDefaultTimezone, getMidnightUTC } from 'index'
import { useCalendar } from 'components/providers/CalendarProvider'
import {
  appendNoteWithMetaData,
  colorFromMetaData,
  META_DATA_LABEL,
  unPackMetaData
} from 'utils/note_meta_data'
import { APP_COLOR } from 'utils/colors'
import { useEventPopover } from 'components/calendar/eventPopover/EventPopoverProvider'
import { eventLengthInDays } from 'utils/api/instance_state'
import { isCypress } from 'utils/cypress'
import { getDefaultCalendar } from 'components/providers/GetChangesProvider'
import { groupToThirdPartyCalendarId } from 'components/calendar/CalendarView'

export interface IEventDetails {
  setStatus: (status: string) => void
  setLoading: (b: boolean) => void
  loading: boolean
  setOpenDiscard: (b: boolean) => void
  openDiscard: boolean
  setOpenAddReminder: (b: boolean) => void
  openAddReminder: boolean
  sendEventToServer(data: EventDoc): Promise<any>
  setStartTime: (date: number) => void
  startTime: string | undefined
  setEndTime: (date: number) => void
  endTime: string | undefined
  setTimeZone: (tz?: string | undefined | null) => void
  timeZone: string
  setRecurrence: (eventRecurrence: EventRecurrence, dirty?: boolean) => void
  reminders: EventReminder[] | undefined
  setReminders: (reminders: EventReminder[]) => void
  RSVP: string | undefined
  setRSVP: (state: string) => void
  notes: EventNote[] | undefined
  setNotes: (notes: EventNote[]) => void
  attendees: Attendee[] | undefined
  setAttendees: (attendees: Attendee[], shouldDirty?: boolean) => void
  attendeeText: string
  setAttendeeText: (t: string) => void
  addAttendee: (attendee: Attendee) => void
  noteText: string
  setNoteText: (t: string) => void
  addNote: (text: string, bytes?: string) => void
  updateNote: (note: EventNote) => void
  participantsStatus: Record<string, EventParticipantStatus> | undefined
  setParticipantStatus: (
    participantsStatus: Record<string, EventParticipantStatus>
  ) => void
  register: UseFormRegister<EventDoc>
  control: Control<EventDoc, any>
  isDirty: boolean
  handleSubmit: UseFormHandleSubmit<EventDoc>
  getValues: UseFormGetValues<EventDoc>
  setLocation: (loc: EventLocation | undefined) => void
  error: string
  setError: (errorText: string) => void
  setGroupID: (id: string) => void
  setSupplementaryGroupIds: (ids: string[]) => void
  supplementaryGroupIds: string[] | undefined
  setAllDay: (allDay: string) => void
  cacheEventData: () => void
  setName: (text: string) => void
  setThirdPartyId: (id: string) => void
  thirdPartyId?: string
  ownerId?: string
  setOwnerId: (id: string) => void
  labels: SimpleLabel[] | undefined
  setLabels: (reminders: SimpleLabel[]) => void
  type: DocType
  setType: (type: DocType) => void
  taskType: TaskType
  setTaskType: (type: TaskType) => void
  someday?: string
  setSomeday: (s: string) => void
  createAtStart: number
  createAtEnd: number
  priority: string
  setPriority: (p: string) => void
  titleSet: boolean
  onTitleSet: (b: boolean) => void
  isNewEvent?: boolean
  groupId?: string
  dirtyFields: any

  setLocationPendingText: (text: string) => void
  locationPendingText: string
  setNotePendingText: (text: string) => void
  notePendingText: string
  addLocationToEvent: (text: string) => Promise<void>
  applyPendingData: () => Promise<void>
  attachements?: Attachment[]
  customUrl?: string
  setAttachments: (attachments: Attachment[] | undefined) => void
  setCustomUrl: (url: string | undefined) => void
  locationReminder: LocationReminder[]
  selectedColor: string | undefined
  setSelectedColor: (color: string) => void
  isLocalSaveOnly: () => Promise<boolean>
}

export function useEventDetails(
  event: Model<EventDoc> | EventDoc,
  group: GroupDoc | undefined,
  createAtStart: number,
  createAtEnd: number,
  doNotScrollToEvent?: boolean
) {
  let pendingData: EventDoc | undefined | null
  const storedData =
    LocalStorage.get(LocalStorageKey.PENDING_EVENT_DATA) ||
    LocalStorage.get(LocalStorageKey.DUPLICATE_EVENT_DATA)

  if (storedData) {
    pendingData = JSON.parse(storedData)
    LocalStorage.remove(LocalStorageKey.DUPLICATE_EVENT_DATA)
    Logger.debug('cached data', pendingData)
  }

  const newEvent = event?._id == undefined || event?._id?.length == 0

  const { closePopover } = useEventPopover()
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    resetField,
    formState: { isDirty, dirtyFields }
  } = useForm<EventDoc>({
    defaultValues: {
      Text: pendingData?.Text ?? event.Text,
      TaskType: pendingData?.TaskType ?? event.TaskType ?? TaskType.meet,
      AllDay: (pendingData?.AllDay ?? event.AllDay) ? event.AllDay : '0',
      TimeZoneNameID:
        (pendingData?.TimeZoneNameID ?? (event.TimeZoneNameID?.length ?? 0) > 0)
          ? event.TimeZoneNameID
          : getDefaultTimezone(),
      StartDate:
        !event.Recurrence && event.late === false
          ? event.StartDate
          : (
              (createAtStart ??
                generateStartEndBasedOnTime(dayjs().unix(), '').start * 1000) /
              1000
            ).toString(),
      EndDate:
        !event.Recurrence && event.late === false
          ? event.EndDate
          : (
              (createAtEnd ??
                generateStartEndBasedOnTime(dayjs().unix(), '').end * 1000) /
              1000
            ).toString(),
      Status: event.Status ?? EventStatus.ACTIVE,
      Recurrence: pendingData?.Recurrence ?? event.Recurrence,
      Location: pendingData?.Location ?? event.Location,
      Reminder: pendingData?.Reminder ?? event.Reminder ?? [],
      Notes: pendingData?.Notes ?? event.Notes ?? [],
      attendees: event.attendees ?? [],
      attendeesChanged: false,
      RequestConfirmation:
        pendingData?.RequestConfirmation ?? event.RequestConfirmation ?? '0',
      GroupID: pendingData?.GroupID ?? event.GroupID,
      ThirdPartyID: pendingData?.ThirdPartyID ?? event.ThirdPartyID,
      PersonalOwnerId: pendingData?.PersonalOwnerId ?? event.PersonalOwnerId,
      OwnerID: pendingData?.OwnerID ?? event.OwnerID,
      Label: pendingData?.Label ?? event.Label,
      Type: pendingData?.Type ?? event.Type,
      someday: pendingData?.someday ?? event.someday,
      Priority: pendingData?.Priority ?? event.Priority,
      createAtStart: createAtStart,
      createAtEnd: createAtEnd,
      noteText: '',
      id: event.id,
      locationPendingText: '',
      notePendingText: '',
      SupplementaryGroupsIDs:
        pendingData?.SupplementaryGroupsIDs ??
        event.SupplementaryGroupsIDs ??
        [],
      Attachments: event.Attachments,
      CustomUrl: event.CustomUrl,
      LocationReminders:
        pendingData?.LocationReminders ?? event.LocationReminders ?? []
    }
  })

  useEffect(() => {
    return () => {
      if (
        (!isDirty && event._id && event._id.length > 0) ||
        (event.local_id && event.local_id.length > 0)
      ) {
        reset({
          Text: event.Text,
          AllDay: event.AllDay ? event.AllDay : '0',
          TimeZoneNameID:
            (event.TimeZoneNameID?.length ?? 0) > 0
              ? event.TimeZoneNameID
              : getDefaultTimezone(),
          StartDate:
            !event.Recurrence && event.late === false
              ? event.StartDate
              : (createAtStart / 1000).toString(),
          EndDate:
            !event.Recurrence && event.late === false
              ? event.EndDate
              : (createAtEnd / 1000).toString(),
          Status: event.Status ?? EventStatus.ACTIVE,
          Recurrence: event.Recurrence,
          Location: event.Location,
          Reminder: event.Reminder ?? [],
          Notes: event.Notes ?? [],
          attendees: event.attendees ?? [],
          attendeesChanged: false,
          RequestConfirmation: event.RequestConfirmation ?? '0',
          GroupID: event.GroupID,
          ThirdPartyID: event.ThirdPartyID,
          Label: event.Label,
          Type: event.Type,
          someday: event.someday,
          Priority: event.Priority,
          createAtStart: createAtStart,
          createAtEnd: createAtEnd,
          TaskType: event.TaskType ?? TaskType.meet,
          noteText: '',
          locationPendingText: '',
          notePendingText: '',
          SupplementaryGroupsIDs: event.SupplementaryGroupsIDs ?? [],
          Attachments: event.Attachments,
          CustomUrl: event.CustomUrl,
          LocationReminders: event.LocationReminders ?? []
        })
      }
    }
  }, [event._rev, event.AllDay])

  function cacheEventData() {
    LocalStorage.set(
      LocalStorageKey.DUPLICATE_EVENT_DATA,
      JSON.stringify({ ...getValues(), newEvent: newEvent })
    )
    LocalStorage.set(
      LocalStorageKey.PENDING_EVENT_DATA,
      JSON.stringify({ ...getValues(), newEvent: newEvent })
    )
  }

  Logger.orange('EventDetails:Values', getValues())

  const [participantsStatus, setParticipantStatus] = useState<
    Record<string, EventParticipantStatus> | undefined
  >(event?.ParticipantsStatus)
  const setGroupID = (id: string) => {
    setValue('GroupID', id, { shouldDirty: true })
    if (newEvent) {
      if (event.syncStatus != 'deleted')
        Database.write(async () => {
          return (await localEventByLocalId(event.id ?? ''))?.update(
            (local) => {
              local.GroupID = getValues('GroupID')
            }
          )
        })
      cacheEventData()
    }
  }
  const setLocation = (location: EventLocation | undefined) => {
    if (location === undefined) {
      setValue('Location', {}, { shouldDirty: true })
    } else {
      setValue('Location', location, { shouldDirty: true })
    }

    cacheEventData()

    setValue('locationPendingText', '', { shouldDirty: true })
  }

  const supplementaryGroupIds = getValues('SupplementaryGroupsIDs')

  const setSupplementaryGroupIds = (ids: string[] | undefined) => {
    setValue('SupplementaryGroupsIDs', ids, { shouldDirty: true })
  }

  const someday = getValues('someday')

  const setSomeday = (someday: string) => {
    setValue('TimeZoneNameID', getDefaultTimezone(), { shouldDirty: false })

    if (someday === '0' && isTrulyEmpty(getValues('StartDate'))) {
      const newStartTime = dayjs(createAtStart)
        .set('minute', 0)
        .set('second', 0)

      setAllDay('0')
      setStartTime(newStartTime.valueOf())
      setEndTime(newStartTime.add(30, 'minute').valueOf())
    }

    setValue('someday', someday, { shouldDirty: true })
    setValue('Recurrence', undefined, { shouldDirty: true })
    setValue('Reminder', [], { shouldDirty: true })

    Database.write(async () => {
      const localEvent = await localEventByLocalId(event.id ?? '')
      if (!localEvent) return

      return localEvent.update((local) => {
        local.someday = someday
      })
    })

    cacheEventData()
  }

  const setTimeZone = (tz: string | undefined | null) => {
    setValue('TimeZoneNameID', tz ?? getDefaultTimezone(), {
      shouldDirty: true
    })
  }
  const setStatus = (status: string) => {
    Logger.pink('EventPopup:setStatus', status)
    setValue('Status', status as EventStatus, { shouldDirty: true })
  }

  const setName = (name: string) => {
    setValue('Text', name, { shouldDirty: true })
    cacheEventData()
  }

  const setThirdPartyId = (id: string) => {
    setValue('ThirdPartyID', id, { shouldDirty: false })
    cacheEventData()
  }

  const setType = (type: DocType) => {
    setValue('Type', type, { shouldDirty: true })
    // setNotes([])
    setPriority('1')
    setRSVP('0')
    if (type === DocType.EVENT) {
      getDefaultCalendar().then((group) => {
        if (group) {
          setGroupID(group._id)
          setTaskType(TaskType.personalEvent)
          const thirdPartyId = groupToThirdPartyCalendarId(group)
          if (thirdPartyId) {
            setThirdPartyId(thirdPartyId)
          }
        }
      })
    } else {
      setGroupID('')
      setThirdPartyId('')
    }
    cacheEventData()
  }

  const setPriority = (priority: string) => {
    setValue('Priority', priority, { shouldDirty: true })
    cacheEventData()
  }

  const [attendeeText, setAttendeeText] = useState<string>('')

  async function updateTimeInDB() {
    //TODO: update time in db after FullCalendar v7
    return
    if (newEvent) {
      closePopover()
      LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)
      console.log('updating time in db')

      if (event.syncStatus != 'deleted')
        Database.write(async () => {
          return (await localEventByLocalId(event.id ?? ''))?.update(
            (local) => {
              local.AllDay = getValues('AllDay')
              local.StartDate = getValues('StartDate')
              local.EndDate = getValues('EndDate')
            }
          )
        })
      cacheEventData()
    }
  }

  const setAllDay = (allDay: string) => {
    const isAllDayNow = allDay === '1'

    setValue('AllDay', allDay, { shouldDirty: true })
    setTimeZone(allDay === '1' ? 'UTC' : getDefaultTimezone())

    if (isAllDayNow) {
      const currentTz = getDefaultTimezone()
      const startDate = dayjs.unix(Number(getValues('StartDate'))).tz(currentTz)
      const endDate = dayjs.unix(Number(getValues('EndDate'))).tz(currentTz)

      const startOfDay = dayjs
        .tz(startDate.format('YYYY-MM-DD'), 'UTC')
        .startOf('day')
      const endOfDay = dayjs
        .tz(endDate.format('YYYY-MM-DD'), 'UTC')
        .startOf('day')

      setStartTime(startOfDay.valueOf())
      setEndTime(endOfDay.valueOf())
    } else {
      const eventStart = dayjs.unix(Number(getValues('StartDate')))
      const eventEnd = dayjs.unix(Number(getValues('EndDate')))
      const durationDays = eventEnd.diff(eventStart, 'day')

      console.log('durationDays', durationDays)

      const startDateUTC = dayjs.unix(Number(getValues('StartDate'))).tz('UTC')

      const nowHour = dayjs()
        .tz(getDefaultTimezone())
        .startOf('hour')
        .add(1, 'hour')
        .hour()
      const nowMinute = 0

      const startOfDay = dayjs
        .tz(startDateUTC.format('YYYY-MM-DD'), getDefaultTimezone())
        .set('hour', nowHour)
        .set('minute', nowMinute)
      const endOfDay = startOfDay
        .clone()
        .add(1, 'hour')
        .add(durationDays, 'day')

      console.log(
        startOfDay.format('YYYY-MM-DD HH:mm:ss'),
        endOfDay.format('YYYY-MM-DD HH:mm:ss')
      )

      setStartTime(startOfDay.valueOf())
      setEndTime(endOfDay.valueOf())
    }
    updateTimeInDB()
  }

  const ownerId = getValues('OwnerID')
  const setOwnerId = (id: string) => {
    console.log('updating owner id')
    setValue('OwnerID', id, { shouldDirty: true })
    cacheEventData()
    if (newEvent) {
      Database.write(async () => {
        return (await localEventByLocalId(event.id ?? ''))?.update((local) => {
          local.OwnerID = id
        })
      })
    }
  }
  const startTime = getValues('StartDate')
  const setStartTime = (time: number) => {
    let zoned = dayjs(time)

    if (getValues('AllDay') === '1') {
      zoned = dayjs(time).tz('UTC')
    }

    Logger.orange('NewStartDate', zoned.format('YYYY-MM-DD HH:mm:ss'))

    let duration = Number(getValues('EndDate')) - Number(getValues('StartDate'))

    let newStartDate = zoned.unix()

    setValue('StartDate', newStartDate.toString(), {
      shouldDirty: true
    })

    if (getValues('TaskType') === TaskType.personalEvent && duration === 0) {
      setValue('EndDate', newStartDate.toString(), {
        shouldDirty: true
      })
    } else {
      setValue('EndDate', (newStartDate + duration).toString(), {
        shouldDirty: true
      })
    }

    updateTimeInDB()
  }
  const taskType = getValues('TaskType') ?? TaskType.meet
  const setTaskType = (type: TaskType) => {
    setValue('TaskType', type, { shouldDirty: true })
    // setNotes([])
    setPriority('1')
    setRSVP('0')
    setThirdPartyId('')
    cacheEventData()
  }
  const endTime = getValues('EndDate')
  const setEndTime = (time: number) => {
    const zoned = dayjs(time).tz(
      getValues('TimeZoneNameID') ?? getDefaultTimezone()
    )

    const duration =
      Number(getValues('EndDate')) - Number(getValues('StartDate'))

    const startDate = dayjs
      .unix(Number(getValues('StartDate')))
      .tz(getValues('TimeZoneNameID') ?? getDefaultTimezone())

    let newEndDateJs = zoned
      .tz(getValues('TimeZoneNameID') ?? getDefaultTimezone())
      .set('hour', zoned.get('hour'))
      .set('minute', zoned.get('minute'))
      .set('date', zoned.get('date'))

    let newEndDate = newEndDateJs.unix()

    Logger.blue(dayjs.unix(newEndDate).format('YYYY-MM-DD HH:mm:ss'))
    Logger.blue(startDate.format('YYYY-MM-DD HH:mm:ss'))

    let isEndDateBeforeStartDate = false

    if (getValues('AllDay') === '1') {
      const endDayOfYear = dayjs.unix(newEndDate).dayOfYear()
      const startDayOfYear = startDate.dayOfYear()
      isEndDateBeforeStartDate = endDayOfYear < startDayOfYear
    } else {
      isEndDateBeforeStartDate = newEndDate < Number(getValues('StartDate'))
    }

    if (isAMPM() && isEndDateBeforeStartDate) {
      if (startDate.get('hour') <= 12 && newEndDateJs.get('hour') <= 12) {
        newEndDate = newEndDateJs.set('hour', zoned.get('hour') + 12).unix()
        isEndDateBeforeStartDate = false
      }
    }

    if (isEndDateBeforeStartDate) {
      setValue('StartDate', (newEndDate - duration).toString(), {
        shouldDirty: true
      })
    }

    setValue('EndDate', newEndDate.toString(), {
      shouldDirty: true
    })
    updateTimeInDB()
  }

  const eventReminders = getValues('Reminder')
  const setEventReminders = (eventReminders: EventReminder[]) => {
    setValue('Reminder', eventReminders, { shouldDirty: true })

    cacheEventData()
  }

  const labels = getValues('Label')
  const setLabels = (labels: SimpleLabel[]) => {
    setValue('Label', labels, { shouldDirty: true })

    cacheEventData()
  }

  const RSVP = getValues('RequestConfirmation')
  const setRSVP = (requestParticipation: string) => {
    setValue('RequestConfirmation', requestParticipation, { shouldDirty: true })
    cacheEventData()
  }
  const notes = getValues('Notes')
  const setNotes = (notes: EventNote[]) => {
    setValue('Notes', notes, { shouldDirty: true })

    if (newEvent)
      Database.write(async () => {
        ;(await localEventByLocalId(event.id ?? ''))?.update((local) => {
          local.Notes = notes
        })
      })
  }
  const attendees = getValues('attendees')
  const setAttendees = (attendees: Attendee[], shouldDirty?: boolean) => {
    setValue(
      'attendeesChanged',
      !arraysAreEqual(event.attendees ?? [], attendees),
      {
        shouldDirty: true
      }
    )
    setValue('attendees', attendees, { shouldDirty: shouldDirty ?? false })
    console.log(attendees)

    cacheEventData()
  }

  const setRecurrence = (eventRecurrence: EventRecurrence, dirty?: boolean) => {
    setValue('Recurrence', eventRecurrence, { shouldDirty: dirty ?? true })
    if (newEvent && typeof event.update === 'function' && dirty) {
      Database.write(async () => {
        event.update((local: { Recurrence: EventRecurrence }) => {
          local.Recurrence = eventRecurrence
        })
      })
    }

    cacheEventData()
  }

  const [loading, setLoading] = useState(false)
  const [openDiscard, setOpenDiscard] = useState(false)
  const [openAddReminder, setOpenAddReminder] = useState(false)
  const [error, setError] = useState('')

  const setRemindersToEvent = (reminders: EventReminder[]) => {
    setOpenAddReminder(false)
    setTimeout(() => {
      setEventReminders(reminders)
    }, 50)
  }
  const calendar = useCalendar()
  const calendarSync = useCalendarSync()

  async function isLocalSaveOnly() {
    const local = await localEventById(event._id ?? '')
    const isConvertedGroup = isConvertedGroupId(getValues('GroupID') ?? '')

    if (isConvertedGroup) return false

    return (
      local?.ThirdPartyID?.length === 0 &&
      Object.keys(dirtyFields).length === 1 &&
      dirtyFields.Reminder !== undefined
    )
  }

  async function sendEventToServer(data: EventDoc) {
    setLoading(true)
    /**
     * This field determines, if an event should be updated locally only, without touching the server
     * For example, we save reminders only locally
     */

    Logger.blue('sendEventToServer:dirtyfields', dirtyFields)
    const shouldSaveLocally = await isLocalSaveOnly()

    await calendarSync
      .sendEventToServer(
        event,
        data,
        shouldSaveLocally,
        isDirty ||
          LocalStorage.get(LocalStorageKey.PENDING_EVENT_DATA) !== null,
        (error) => {
          setError(error)
        }
      )
      .then(() => {
        setTimeout(() => {
          if (!isCypress()) {
            const newDate = dayjs(Number(data.StartDate) * 1000).tz(
              data.TimeZoneNameID
            )
            if (
              newDate.isAfter(dayjs(calendar.toISO)) ||
              newDate.isBefore(dayjs(calendar.fromISO))
            ) {
              calendar.onGoToDate(newDate.format('YYYY-MM-DD'))
            }
            calendar.onScrollToTime(newDate)
          }
        }, 250)
      })
  }

  const [titleSet, onTitleSet] = useState(false)

  const addNoteToArray = (noteText: string, bytes?: string) => {
    if (!noteText) return

    let currentNotes = getValues('Notes')

    if (typeof currentNotes === 'string') {
      currentNotes = []
    }

    const isNoteWithMetaData = currentNotes?.find((note) =>
      note.Note.includes(META_DATA_LABEL)
    )

    if (isNoteWithMetaData) {
      const updatedNote = currentNotes?.map((note) => {
        if (note.Note.includes(META_DATA_LABEL)) {
          return {
            ...note,
            Note: appendNoteWithMetaData(noteText, unPackMetaData(event))
          }
        }

        return note
      })

      setNotes(updatedNote ?? [])
    } else {
      const newNotes = [...(currentNotes ?? [])]
      const newNote: EventNote = {
        FilePath: 'null',
        Note: noteText,
        NoteID: Date.now(),
        Status: 1,
        FileBytes: bytes
      }

      if (getValues('Type') === DocType.NOTE && !titleSet) {
        let updatedText = noteText
          .replace(/<\/?[^>]+(>|$)/g, '')
          .substring(0, 25)

        if (noteText.length > 25) {
          updatedText += '...'
        }
        if (updatedText != '...') setName(updatedText)
      }

      newNotes.push(newNote)
      setNotes(newNotes)
    }
  }

  const updateNote = (updatedNote: EventNote) => {
    const currentNotes = getValues('Notes')
    if (currentNotes) {
      const updated = currentNotes.map((note) => {
        if (note.NoteID === updatedNote.NoteID) return updatedNote

        return note
      })
      setNotes(updated)
      const noteText = updatedNote.Note

      if (getValues('Type') === DocType.NOTE && !titleSet) {
        let updatedText = noteText
          .replace(/<\/?[^>]+(>|$)/g, '')
          .substring(0, 25)

        if (noteText.length > 25) {
          updatedText += '...'
        }

        if (updatedText != '...') setName(updatedText)
      }
    }
  }

  const addAttendeeToArray = (newAttendee: Attendee) => {
    if (
      !newAttendee ||
      attendees?.find((attendee) => attendee.email === newAttendee.email)
    )
      return

    const newAttendees = [...(getValues('attendees') ?? [])]

    Database.getCollection<ParticipantSuggestion>(
      CollectionType.PARTICIPANT_SUGGESTION
    )
      .query()
      .fetch()
      .then((participants) => {
        const participant = participants.find(
          (p) => p.email === newAttendee.email
        )
        if (!participant && newAttendee.email) {
          Database.write(async () => {
            await Database.getCollection<ParticipantSuggestion>(
              CollectionType.PARTICIPANT_SUGGESTION
            ).create((p) => {
              p.email = newAttendee.email!
              p.name = newAttendee.displayName ?? ''
              p.img = ''
            })
          })
        }
      })

    const provider = groupToProvider(group)
    if (provider === ProviderType.GOOGLE || provider === ProviderType.APPLE) {
      //first add yourself if not exist
      if (
        attendees?.find(
          (attendee) =>
            attendee.email === removePrefix(group?.CalendarEmail ?? '')
        ) === undefined
      ) {
        newAttendees.push({
          email: removePrefix(group?.CalendarEmail ?? ''),
          responseStatus: 'accepted',
          self: true
        })
      }
    }

    if (!newAttendees?.find((attendee) => attendee.email === newAttendee.email))
      newAttendees.push(newAttendee)

    setAttendeeText('')
    setAttendees(newAttendees)
  }
  const noteText = getValues('noteText')
  const setNoteText = (text: string) => {
    setValue('noteText', text, { shouldDirty: false })
  }

  const attachements = getValues('Attachments')
  const setAttachments = (attachments: Attachment[] | undefined) => {
    setValue('Attachments', attachments, { shouldDirty: true })
  }
  const customUrl = getValues('CustomUrl')
  const setCustomUrl = (url: string | undefined) => {
    setValue('CustomUrl', url, { shouldDirty: true })
  }

  const setLocationPendingText = (text: string) => {
    setValue('locationPendingText', text, { shouldDirty: true })
  }

  const notePendingText = getValues('notePendingText')
  const setNotePendingText = (text: string) => {
    setValue('notePendingText', text, { shouldDirty: true })
  }

  const addLocationToEvent = (text: string) => {
    return googleMapsService.fromAddress(text).then(
      (response) => {
        const { lat, lng } = response.results[0].geometry.location
        setLocation({
          FilteredLocation: 'null',

          Address: text,
          Lat: String(lat),
          Long: String(lng)
        })
      },
      (error) => {
        setLocation({
          FilteredLocation: 'null',

          Address: text,
          Lat: '0',
          Long: '0'
        })
        console.log(getValues())
      }
    )
  }

  const applyPendingData = async () => {
    const locationPendingText = getValues('locationPendingText')
    const notePendingText = getValues('notePendingText')

    if (
      locationPendingText.length > 0 &&
      getValues('Location')?.Address !== locationPendingText
    ) {
      await addLocationToEvent(locationPendingText)
    }
    if (notePendingText.length > 0) {
      addNoteToArray(notePendingText)
    }
  }

  const [selectedColor, setSelectedColor] = useState<string | undefined>(
    colorFromMetaData(event) ?? event.Color ?? APP_COLOR
  )

  const eventPopupInterface: IEventDetails = {
    setLoading: setLoading,
    loading: loading,
    sendEventToServer: sendEventToServer,
    startTime: startTime,
    setStartTime: setStartTime,
    endTime: endTime,
    setEndTime: setEndTime,
    setTimeZone: setTimeZone,
    timeZone: getValues('TimeZoneNameID') ?? getDefaultTimezone(),
    reminders: eventReminders,
    setReminders: setRemindersToEvent,
    RSVP: RSVP,
    setRSVP: setRSVP,
    notes: notes,
    setNotes: setNotes,
    setRecurrence: setRecurrence,
    setOpenDiscard: setOpenDiscard,
    openDiscard: openDiscard,
    setOpenAddReminder: setOpenAddReminder,
    openAddReminder: openAddReminder,
    setAttendees,
    attendeeText: attendeeText,
    setAttendeeText: setAttendeeText,
    addAttendee: addAttendeeToArray,
    addNote: addNoteToArray,
    participantsStatus: participantsStatus,
    setParticipantStatus,
    setStatus,
    register,
    isDirty,
    handleSubmit,
    getValues,
    control,
    setLocation,
    error,
    setError,
    setGroupID,
    attendees,
    setAllDay,
    cacheEventData,
    setName,
    setThirdPartyId,
    thirdPartyId: getValues('ThirdPartyID'),
    labels,
    setLabels,
    type: getValues('Type') ?? (is24meApp() ? DocType.TASK : DocType.EVENT),
    setType,
    updateNote,
    someday,
    setSomeday,
    createAtStart,
    createAtEnd,
    priority: getValues('Priority') ?? '0',
    setPriority: setPriority,
    titleSet,
    onTitleSet,
    noteText: noteText,
    setNoteText,
    isNewEvent: newEvent,
    ownerId,
    setOwnerId,
    groupId: getValues('GroupID'),
    dirtyFields,
    locationPendingText: getValues('locationPendingText'),
    setLocationPendingText,
    setNotePendingText,
    notePendingText: getValues('notePendingText'),
    addLocationToEvent,
    applyPendingData,
    supplementaryGroupIds,
    setSupplementaryGroupIds,
    attachements,
    setAttachments,
    customUrl,
    setCustomUrl,
    locationReminder: getValues('LocationReminders') ?? [],
    selectedColor,
    setSelectedColor,
    isLocalSaveOnly,
    taskType: taskType,
    setTaskType: setTaskType
  }

  return eventPopupInterface
}

function arraysAreEqual(a: any[], b: any[]): boolean {
  return isEqual(a, b)
}
